import React from "react";
import { apiServices } from "./_services/api.services";

const PAGE_SIZE = 100;

export class SqlConsole extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            sql: "",
            results: null,
            loading: false,
            error: null,
            page: 0,
            history: [],
            showHistory: false,
        };
        this.execute = this.execute.bind(this);
        this.loadHistory = this.loadHistory.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    async execute(page = 0) {
        const { sql } = this.state;
        if (!sql.trim()) return;

        this.setState({ loading: true, error: null, results: null, page });

        try {
            const resp = await apiServices.axios.post('/db/execute', {
                sql,
                offset: page * PAGE_SIZE,
                limit: PAGE_SIZE,
            });
            this.setState({ results: resp.data, loading: false });
        } catch (e) {
            const msg = (e.response && e.response.data && e.response.data.error) || e.message || "Unknown error";
            this.setState({ error: msg, loading: false });
        }
    }

    async loadHistory() {
        try {
            const resp = await apiServices.axios.get('/db/audit');
            this.setState({ history: resp.data, showHistory: true });
        } catch (e) {
            const msg = (e.response && e.response.data && e.response.data.error) || e.message || "Unknown error";
            this.setState({ error: msg });
        }
    }

    onKeyDown(e) {
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            this.execute(0);
        }
    }

    renderCell(cell) {
        if (cell === null || cell === undefined) {
            return <em className="tx-color-03">NULL</em>;
        }
        if (typeof cell === "object") {
            return <code>{JSON.stringify(cell)}</code>;
        }
        return String(cell);
    }

    renderPager() {
        const { results, page, loading } = this.state;
        if (!results) return null;

        const anyTruncated = results.some(r => r.type === "query" && r.truncated);
        if (page === 0 && !anyTruncated) return null;

        return (
            <div className="d-flex align-items-center mg-b-15">
                <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={loading || page === 0}
                    onClick={() => this.execute(page - 1)}
                >
                    Previous
                </button>
                <span className="mg-x-15 tx-color-03">Page {page + 1}</span>
                <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={loading || !anyTruncated}
                    onClick={() => this.execute(page + 1)}
                >
                    Next
                </button>
                {anyTruncated && (
                    <span className="mg-l-15 tx-color-03 tx-12">Results capped at {PAGE_SIZE} rows — paginate or add a LIMIT</span>
                )}
            </div>
        );
    }

    renderResults() {
        const { results, error } = this.state;

        if (error) {
            return (
                <div className="alert alert-danger mg-t-20">
                    <strong>Error:</strong> {error}
                </div>
            );
        }

        if (!results || !results.length) return null;

        return (
            <div className="mg-t-20">
                {this.renderPager()}
                {results.map((r, i) => (
                    <div key={i} className="mg-b-20">
                        {r.type === "update" ? (
                            <div className="alert alert-success">
                                {r.affectedRows} row(s) affected
                            </div>
                        ) : (
                            <div>
                                <div className="tx-color-03 tx-12 mg-b-10">
                                    {r.rowCount} row(s) returned{r.truncated ? " (truncated)" : ""}
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-striped table-sm">
                                        <thead>
                                            <tr>
                                                {r.columns.map((c, j) => (
                                                    <th key={j}>{c}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {r.rows.map((row, j) => (
                                                <tr key={j}>
                                                    {row.map((cell, k) => (
                                                        <td key={k}>{this.renderCell(cell)}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    renderHistory() {
        const { history, showHistory } = this.state;
        if (!showHistory) return null;

        return (
            <div className="mg-t-30">
                <h6 className="mg-b-10">Recent queries</h6>
                <div className="table-responsive">
                    <table className="table table-striped table-sm tx-13">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Duration</th>
                                <th>Status</th>
                                <th>Query</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map(h => (
                                <tr key={h.id}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => this.setState({ sql: h.sql, showHistory: false })}
                                    title="Click to load into editor">
                                    <td className="tx-nowrap">{new Date(h.executed_at).toLocaleString()}</td>
                                    <td>{h.duration_ms}ms</td>
                                    <td>{h.error
                                        ? <span className="tx-danger" title={h.error}>error</span>
                                        : <span className="tx-success">ok</span>}</td>
                                    <td><code>{h.sql.length > 120 ? h.sql.substring(0, 120) + "…" : h.sql}</code></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    render() {
        const { sql, loading, showHistory } = this.state;

        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <h4 className="mg-b-20">SQL Console</h4>
                    <div className="form-group">
                        <textarea
                            className="form-control"
                            rows="12"
                            placeholder="Paste SQL here... (Ctrl+Enter to execute)"
                            value={sql}
                            onChange={e => this.setState({ sql: e.target.value })}
                            onKeyDown={this.onKeyDown}
                            style={{ fontFamily: "monospace", fontSize: "13px" }}
                        />
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={() => this.execute(0)}
                        disabled={loading || !sql.trim()}
                    >
                        {loading ? "Executing..." : "Execute"}
                    </button>
                    <button
                        className="btn btn-outline-primary mg-l-10"
                        onClick={() => showHistory
                            ? this.setState({ showHistory: false })
                            : this.loadHistory()}
                    >
                        {showHistory ? "Hide history" : "History"}
                    </button>
                    {this.renderResults()}
                    {this.renderHistory()}
                </div>
            </div>
        );
    }
}
